import { z } from "zod"

import { ApprovalStatus, PriorityStatus } from "@/graphql/codegen/graphql"

export const locationRowSchema = z.object({
  shipToLocationId: z.string().min(1, "Location is required"),
  customerCode: z.array(z.string()).default([]),
  priorityStatus: z.nativeEnum(PriorityStatus).nullable().optional(),
  approvalStatus: z.nativeEnum(ApprovalStatus).nullable().optional(),
})

export function createAssociateProductFormSchema(existingLocationIds: string[] = []): z.ZodObject<{
  productId: z.ZodNumber
  customerDescription: z.ZodOptional<z.ZodString>
  customerCode: z.ZodDefault<z.ZodArray<z.ZodString>>
  locations: z.ZodEffects<z.ZodDefault<z.ZodArray<typeof locationRowSchema>>>
}> {
  return z.object({
    productId: z.number({ required_error: "Product is required" }),
    customerDescription: z.string().optional(),
    customerCode: z.array(z.string()).default([]),
    locations: z
      .array(locationRowSchema)
      .default([])
      .superRefine((locs, ctx) => {
        const seen = new Set<string>()
        locs.forEach((loc, i) => {
          if (loc.shipToLocationId && seen.has(loc.shipToLocationId)) {
            ctx.addIssue({
              code: z.ZodIssueCode.custom,
              message: "This location has already been added",
              path: [i, "shipToLocationId"],
            })
          }
          if (loc.shipToLocationId && existingLocationIds.includes(loc.shipToLocationId)) {
            ctx.addIssue({
              code: z.ZodIssueCode.custom,
              message: "This location has already been associated",
              path: [i, "shipToLocationId"],
            })
          }
          seen.add(loc.shipToLocationId)
        })
      }),
  })
}

export const associateProductFormSchema = createAssociateProductFormSchema()

export type AssociateProductFormValues = z.infer<typeof associateProductFormSchema>

export function createAssociationLocationFormSchema(
  existingLocationIds: string[] = []
): z.ZodObject<{ locations: z.ZodEffects<z.ZodDefault<z.ZodArray<typeof locationRowSchema>>> }> {
  return z.object({
    locations: z
      .array(locationRowSchema)
      .default([])
      .superRefine((locs, ctx) => {
        const seen = new Set<string>()
        locs.forEach((loc, i) => {
          if (loc.shipToLocationId && seen.has(loc.shipToLocationId)) {
            ctx.addIssue({
              code: z.ZodIssueCode.custom,
              message: "This location has already been added",
              path: [i, "shipToLocationId"],
            })
          }
          if (loc.shipToLocationId && existingLocationIds.includes(loc.shipToLocationId)) {
            ctx.addIssue({
              code: z.ZodIssueCode.custom,
              message: "This location has already been associated",
              path: [i, "shipToLocationId"],
            })
          }
          seen.add(loc.shipToLocationId)
        })
      }),
  })
}

export type AssociationLocationFormValues = z.infer<ReturnType<typeof createAssociationLocationFormSchema>>
